type RequiredParamKeys<S extends string> =
  S extends `${string}:${infer K}/${infer Rest}`
    ? K | RequiredParamKeys<Rest>
    : S extends `${string}:${infer _}?/${infer Rest}`
      ? RequiredParamKeys<Rest>
      : S extends `${string}:${infer _}?`
        ? never
        : S extends `${string}:${infer K}`
          ? K
          : never;

type OptionalParamKeys<S extends string> =
  S extends `${string}:${infer _}/${infer Rest}`
    ? OptionalParamKeys<Rest>
    : S extends `${string}:${infer K}?/${infer Rest}`
      ? K | OptionalParamKeys<Rest>
      : S extends `${string}:${infer K}?`
        ? K
        : never;

type ParamsOf<S extends string> = Record<RequiredParamKeys<S>, string> &
  Partial<Record<OptionalParamKeys<S>, string>>;

type ReplaceParams<S extends string> =
  S extends `${infer H}:${string}/${infer T}`
    ? `${H}${string}/${ReplaceParams<T>}`
    : S extends `${infer H}:${string}`
      ? `${H}${string}`
      : S;

export type PathwayRouteFunction<TRoute extends string> = (
  params: ParamsOf<TRoute>,
) => ReplaceParams<TRoute>;

/** Pathway routes. */
export const routes = {
  plans: {
    managePlansEdit: (({ planId }) =>
      `/plans/manage-plans/${planId}/edit`) as PathwayRouteFunction<'/plans/manage-plans/:planId/edit'>,
    plan: (({ planId }) =>
      `/plans/my-plans/${planId}`) as PathwayRouteFunction<'/plans/my-plans/:planId'>,
    managedQuiz: (({ quizId }) =>
      `/plans/manage-quizzes/${quizId}`) as PathwayRouteFunction<'/plans/manage-quizzes/:quizId'>,
    managePlanTeamMembers: (({ planId, teamMemberId }) =>
      `/plans/manage-plans/${planId}/team-member/${teamMemberId}`) as PathwayRouteFunction<'/plans/manage-plans/:planId/team-member/:teamMemberId'>,
    managePlan: (({ planId }) =>
      `/plans/manage-plans/${planId}`) as PathwayRouteFunction<'/plans/manage-plans/:planId'>,
    trainTogether: (({ planId }) =>
      `/plans/manage-plans/${planId}/train-together`) as PathwayRouteFunction<'/plans/manage-plans/:planId/train-together'>,
    planDocument: (({ planId, docId }) =>
      `/plans/my-plans/${planId}/doc/${docId}`) as PathwayRouteFunction<'/plans/my-plans/:planId/doc/:docId'>,
    addReadOnlyPlan: (({ planId }) =>
      `/plans/add/${planId}`) as PathwayRouteFunction<'/plans/add/:planId'>,
    plans: '/plans/my-plans' as const,
    managedPlans: '/plans/manage-plans' as const,
    managedQuizzes: '/plans/manage-quizzes' as const,
    managedQuizzesNew: '/plans/manage-quizzes/new' as const,
  },
  reports: {
    plans: '/reports/plans' as const,
    teamMembers: '/reports/team-members' as const,
    planProgress: (({ planId, compliancePlan }) =>
      compliancePlan
        ? `/reports/plans/${planId}/${compliancePlan}`
        : `/reports/plans/${planId}`) as PathwayRouteFunction<'/reports/plans/:planId/:compliancePlan?'>,
    teamMemberPlanProgress: (({ planId, teamMemberId }) =>
      `/reports/plans/${planId}/team-member/${teamMemberId}`) as PathwayRouteFunction<'/reports/plans/:planId/team-member/:teamMemberId'>,
    planTeamMemberProgress: (({ planId, teamMemberId }) =>
      `/reports/team-members/${teamMemberId}/plan/${planId}`) as PathwayRouteFunction<'/reports/team-members/:teamMemberId/plan/:planId'>,
    teamMemberProgress: (({ teamMemberId }) =>
      `/reports/team-members/${teamMemberId}`) as PathwayRouteFunction<'/reports/team-members/:teamMemberId'>,
  },
};
